// src/constants/contactTypes.js

// Contact Types - Personal vs Professional
export const CONTACT_TYPES = {
  PERSONAL: 'personal',
  PROFESSIONAL: 'professional'
};

// Personal Contact Categories
export const PERSONAL_CATEGORIES = {
  FAMILY: { id: 'family', label: 'Family', icon: 'Users', color: 'blue' },
  FRIEND: { id: 'friend', label: 'Friend', icon: 'Heart', color: 'pink' },
  ACQUAINTANCE: { id: 'acquaintance', label: 'Acquaintance', icon: 'UserPlus', color: 'gray' },
  MENTOR: { id: 'mentor', label: 'Mentor', icon: 'GraduationCap', color: 'purple' },
  PERSONAL_SERVICE: { id: 'personal_service', label: 'Personal Service', icon: 'Wrench', color: 'amber' }
};

// Professional Contact Categories
export const PROFESSIONAL_CATEGORIES = {
  TEAM: { id: 'team', label: 'Team / Colleague', icon: 'Users', color: 'indigo' },
  CLIENT: { id: 'client', label: 'Client', icon: 'Briefcase', color: 'green' },
  LEAD: { id: 'lead', label: 'Lead / Prospect', icon: 'Target', color: 'orange' },
  SUPPLIER: { id: 'supplier', label: 'Supplier', icon: 'Package', color: 'cyan' },
  PARTNER: { id: 'partner', label: 'Partner / Stakeholder', icon: 'Handshake', color: 'violet' },
  ORGANISATION: { id: 'organisation', label: 'Organisation Contact', icon: 'Building2', color: 'slate' }
};

// All categories combined
export const ALL_CATEGORIES = {
  ...PERSONAL_CATEGORIES,
  ...PROFESSIONAL_CATEGORIES,
  OTHER: { id: 'other', label: 'Other', icon: 'MoreHorizontal', color: 'gray' }
};

// Communication Channels
export const COMMUNICATION_CHANNELS = {
  PHONE: { id: 'phone', label: 'Phone', icon: 'Phone', actionPrefix: 'tel:' },
  EMAIL: { id: 'email', label: 'Email', icon: 'Mail', actionPrefix: 'mailto:' },
  WHATSAPP: { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', actionPrefix: 'https://wa.me/' },
  TELEGRAM: { id: 'telegram', label: 'Telegram', icon: 'Send', actionPrefix: 'https://t.me/' },
  WEBSITE: { id: 'website', label: 'Website', icon: 'Globe', actionPrefix: '' },
  ZOOM: { id: 'zoom', label: 'Zoom', icon: 'Video', actionPrefix: '' },
  TEAMS: { id: 'teams', label: 'MS Teams', icon: 'Video', actionPrefix: '' },
  GOOGLE_MEET: { id: 'google_meet', label: 'Google Meet', icon: 'Video', actionPrefix: '' }
};

// Social Platforms
export const SOCIAL_PLATFORMS = {
  LINKEDIN: { id: 'linkedin', label: 'LinkedIn', icon: 'Linkedin', color: '#0077b5' },
  TWITTER: { id: 'twitter', label: 'X (Twitter)', icon: 'Twitter', color: '#1da1f2' },
  FACEBOOK: { id: 'facebook', label: 'Facebook', icon: 'Facebook', color: '#1877f2' },
  INSTAGRAM: { id: 'instagram', label: 'Instagram', icon: 'Instagram', color: '#e4405f' },
  YOUTUBE: { id: 'youtube', label: 'YouTube', icon: 'Youtube', color: '#ff0000' },
  GITHUB: { id: 'github', label: 'GitHub', icon: 'Github', color: '#333' }
};

// Contact Visibility Options
export const CONTACT_VISIBILITY = {
  PRIVATE: { id: 'private', label: 'Private', description: 'Only you can see this contact' },
  WORKSPACE: { id: 'workspace', label: 'Workspace', description: 'Visible to workspace members' },
  ORGANISATION: { id: 'organisation', label: 'Organisation', description: 'Visible to entire organisation' },
  PUBLIC: { id: 'public', label: 'Public', description: 'Visible to all book users' }
};

// Default Contact Data Structure
export const DEFAULT_CONTACT = {
  // Basic Info
  name: '',
  contactType: CONTACT_TYPES.PERSONAL,
  category: 'friend',
  description: '',
  avatarColor: 'bg-indigo-500',
  photoUrl: '',
  
  // Communication
  email: '',
  phone: '',
  channels: {
    whatsapp: '',
    telegram: '',
    website: ''
  },
  meetingLinks: {
    zoom: '',
    teams: '',
    googleMeet: ''
  },
  
  // Social Profiles
  socials: {
    linkedin: '',
    twitter: '',
    facebook: '',
    instagram: ''
  },
  
  // Professional Info
  professional: {
    company: '',
    department: '',
    section: '',
    position: '',
    workLocation: '',
    workHours: '',
    reportsTo: ''
  },
  
  // Address
  address: {
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: ''
  },
  
  // Organisation / Workspace
  workspaceId: null,
  organisationId: null,
  departmentId: null,
  visibility: 'private',
  
  // Metadata
  tags: [],
  groups: [],
  isFavorite: false,
  isArchived: false,
  
  // Linking
  linkedMeetings: [],
  linkedProjects: [],
  linkedNotes: [],
  
  // System
  createdBy: '',
  createdAt: null,
  updatedAt: null
};

// Contact Group Structure
export const DEFAULT_CONTACT_GROUP = {
  id: '',
  name: '',
  description: '',
  color: 'indigo',
  icon: 'Users',
  contactIds: [],
  workspaceId: null,
  visibility: 'private',
  isSystem: false, // System groups like "All Contacts", "Favorites"
  createdBy: '',
  createdAt: null,
  updatedAt: null
};

// Quick Action Types
export const CONTACT_ACTIONS = {
  CALL: { id: 'call', label: 'Call', icon: 'Phone', color: 'green' },
  EMAIL: { id: 'email', label: 'Email', icon: 'Mail', color: 'blue' },
  WHATSAPP: { id: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', color: 'green' },
  MEETING: { id: 'meeting', label: 'Schedule Meeting', icon: 'Calendar', color: 'purple' },
  TASK: { id: 'task', label: 'Create Task', icon: 'CheckSquare', color: 'orange' },
  NOTE: { id: 'note', label: 'Add Note', icon: 'FileText', color: 'gray' },
  NAVIGATE: { id: 'navigate', label: 'Navigate', icon: 'MapPin', color: 'red' },
  COPY: { id: 'copy', label: 'Copy Details', icon: 'Copy', color: 'slate' }
};

// Helper Functions
export const getCategoryInfo = (categoryId) => {
  return ALL_CATEGORIES[categoryId?.toUpperCase()] || ALL_CATEGORIES.OTHER;
};

export const getContactTypeLabel = (type) => {
  return type === CONTACT_TYPES.PROFESSIONAL ? 'Professional' : 'Personal';
};

export const getCategoriesByType = (type) => {
  if (type === CONTACT_TYPES.PROFESSIONAL) {
    return Object.values(PROFESSIONAL_CATEGORIES);
  }
  return Object.values(PERSONAL_CATEGORIES);
};

export const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return '';
  // Remove all non-numeric except +
  return phone.replace(/[^\d+]/g, '').replace(/^\+/, '');
};

export const getAvailableActions = (contact) => {
  const actions = [];
  
  if (contact.phone) {
    actions.push(CONTACT_ACTIONS.CALL);
    actions.push(CONTACT_ACTIONS.WHATSAPP);
  }
  if (contact.email) {
    actions.push(CONTACT_ACTIONS.EMAIL);
  }
  if (contact.address?.street || contact.address?.city) {
    actions.push(CONTACT_ACTIONS.NAVIGATE);
  }
  
  // Always available
  actions.push(CONTACT_ACTIONS.MEETING);
  actions.push(CONTACT_ACTIONS.TASK);
  actions.push(CONTACT_ACTIONS.NOTE);
  actions.push(CONTACT_ACTIONS.COPY);
  
  return actions;
};